import { indicesOfHiddenColumns } from '../../../common/reportConfiguration/effects/reportQueryEffectResponders';
import { EReportColumnTypes, getColumnName, getDisplayName } from '@opengov/reporting-types/src/reportColumns-immer';
import { getCondBreakdown, getCalculationString } from '../../../common/modals/components/utilities';
import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';
import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { getFilterParamsByColumnType } from './selectReportDataTableProps.utils';
import { createSelector } from 'reselect';
import { selectReportConfigWithMetadata } from '../../selectors/selectReportExplorerState';
import { reportLocaleText } from '../../../common/locale';
import { selectAgGridColumnConfig } from '../../../common/reportConfiguration/reportColumnSelectors';
import { selectFilterValuesByColumn } from '../../ReportDataTableAgGrid/selectors/selectSelectedAgGridColumn';
import { IReportConfiguration, IAgGridColumnConfig } from '@opengov/reporting-types';
import { OrderedMap } from 'immutable';
import { LocaleText } from '../../../../src/locale';

const HEADER_LENGTH_MAX = 20;
const getColumnDefs = ({
  reportConfiguration,
  locale,
  agGridColumnConfig,
  filterValuesByColumn
}: {
  locale: LocaleText;
  filterValuesByColumn: OrderedMap<string, OrderedMap<string, number>>;
  reportConfiguration?: IReportConfiguration;
  agGridColumnConfig?: IAgGridColumnConfig[];
}) => {
  if (!reportConfiguration) return [];

  const columnRenames = reportConfiguration.presentationConfiguration.columnNames;
  const { columns } = reportConfiguration.detailTableQuery;
  // selectReportConfigWithMetadata will always provide the most up to date column metadata
  // for both the lens/view source columns and the joined dimensional columns
  const removableIndices = indicesOfHiddenColumns(reportConfiguration.detailTableQuery.columns);

  const filteredColumns = columns
    .filter((_, ndx) => !removableIndices.includes(ndx))
    .map((column) => {
      // look up the existing column config based on the column name
      // if that fails, fall back to the simple name
      const agGridConfigForColumn =
        agGridColumnConfig?.find((col) => col.columnName === column.name) ||
        agGridColumnConfig?.find((col) => col.columnName === getColumnName(column.name));
      const columnType = getTableColumnTypeFromReportColumnType(column);
      const { filter, filterParams } = getFilterParamsByColumnType(columnType, { filterValuesByColumn, column });
      let tooltipContent: string | null = null;
      if (
        getDisplayName(column, columnRenames).length > HEADER_LENGTH_MAX ||
        column.columnType === EReportColumnTypes.conditional ||
        column.columnType === EReportColumnTypes.infix
      ) {
        if (column.columnType === EReportColumnTypes.conditional) {
          tooltipContent = `${locale.reportTable.columnEdit.condHeader}: ${String(getCondBreakdown(column, locale))}`;
        } else if (column.columnType === EReportColumnTypes.infix) {
          tooltipContent = `${locale.reportTable.columnEdit.calcHeader}: ${String(getCalculationString(column))}`;
        } else {
          tooltipContent = getDisplayName(column, columnRenames);
        }
      }

      return {
        headerName: getDisplayName(column, columnRenames),
        colId: column.name,
        field: column.name,
        type: columnType,
        cellClass: columnType !== ColumnTypes.Text ? 'ag-right-aligned-cell' : '',
        headerClass: columnType !== ColumnTypes.Text ? 'ag-right-aligned-header' : '',
        // ag-grid v34: tooltipValueGetter applies only to cells; header tooltips use headerTooltip.
        tooltipValueGetter: () => tooltipContent,
        headerTooltip: tooltipContent ?? undefined,
        pinned: agGridConfigForColumn?.pinned as boolean | 'left' | 'right' | undefined,
        hide: agGridConfigForColumn?.hide,
        sortedIndex: agGridConfigForColumn?.sortedIndex ?? 0,
        filter: filter,
        filterParams:
          typeof filter === 'string'
            ? { ...filterParams, buttons: ['reset', 'apply'], closeOnApply: true }
            : filterParams
      };
    })
    // sort the columns according to the stored sort order
    .sort(({ sortedIndex: a }, { sortedIndex: b }) => a - b)
    // and then remove the sortedIndex property to avoid an avalanche of console warnings from ag-grid
    .map(({ sortedIndex: _, ...rest }) => rest);

  return filteredColumns;
};

export const selectReportDataTableColumnDefsWithGroupByColumn = createSelector(
  selectReportConfigWithMetadata,
  reportLocaleText,
  selectAgGridColumnConfig,
  selectFilterValuesByColumn,
  (reportConfiguration, locale, agGridColumnConfig, filterValuesByColumn) => {
    let filteredColumns = getColumnDefs({
      reportConfiguration,
      locale,
      agGridColumnConfig,
      filterValuesByColumn
    });
    const groupByColumn = filteredColumns.find((column) =>
      reportConfiguration?.detailTableQuery.groupByColumns?.includes(column.field)
    );
    filteredColumns = filteredColumns.filter(
      (column) => !reportConfiguration?.detailTableQuery.groupByColumns?.includes(column.field)
    );
    if (groupByColumn) {
      filteredColumns.unshift(groupByColumn);
    }
    return filteredColumns;
  }
);

export const selectReportDataTableColumnDefs = createSelector(
  selectReportConfigWithMetadata,
  reportLocaleText,
  selectAgGridColumnConfig,
  selectFilterValuesByColumn,
  (reportConfiguration, locale, agGridColumnConfig, filterValuesByColumn) => {
    const filteredColumns = getColumnDefs({
      reportConfiguration,
      locale,
      agGridColumnConfig,
      filterValuesByColumn
    });
    return filteredColumns.filter(
      (column) => !reportConfiguration?.detailTableQuery.groupByColumns?.includes(column.field)
    );
  }
);
